import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import reportsAnalyticsService from '../services/reportsAnalyticsService';
import classesService from '../services/classesService';
import campusService from '../services/campusService';
import schoolsService from '../services/schoolsService';
import { Download, ChartBar } from '@phosphor-icons/react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const TABS = [
  { key: 'attendance', label: 'Attendance' },
  { key: 'fee-collection', label: 'Fee Collection' },
  { key: 'academic-performance', label: 'Academic Performance' },
  { key: 'enrollment', label: 'Enrollment' },
];

const today = () => new Date().toISOString().split('T')[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

function ReportsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('attendance');
  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    start_date: monthAgo(),
    end_date: today(),
    class_id: '',
    campus_id: '',
    fee_type: '',
    academic_term_id: '',
  });

  useEffect(() => {
    classesService.listClasses().then(data => setClasses(data || [])).catch(() => {});
    campusService.listCampuses().then(data => setCampuses(Array.isArray(data) ? data : [])).catch(() => setCampuses([]));
    if (user?.school_id) {
      schoolsService.listTerms(user.school_id).then(data => {
        const list = data || [];
        setTerms(list);
        const current = list.find(t => t.is_current);
        setFilters(prev => ({ ...prev, academic_term_id: prev.academic_term_id || (current ? current.id : (list[0]?.id || '')) }));
      }).catch(() => {});
    }
  }, [user]);

  const buildParams = useCallback(() => {
    const p = { class_id: filters.class_id || undefined, campus_id: filters.campus_id || undefined };
    if (activeTab === 'attendance' || activeTab === 'fee-collection') {
      p.start_date = filters.start_date;
      p.end_date = filters.end_date;
    }
    if (activeTab === 'fee-collection') {
      p.fee_type = filters.fee_type || undefined;
    }
    if (activeTab === 'academic-performance') {
      p.academic_term_id = filters.academic_term_id;
    }
    return p;
  }, [activeTab, filters]);

  const runReport = async () => {
    if (activeTab === 'academic-performance' && !filters.academic_term_id) {
      toast.error('Select an academic term');
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const params = buildParams();
      let data;
      if (activeTab === 'attendance') data = await reportsAnalyticsService.attendance(params);
      else if (activeTab === 'fee-collection') data = await reportsAnalyticsService.feeCollection(params);
      else if (activeTab === 'academic-performance') data = await reportsAnalyticsService.academicPerformance(params);
      else data = await reportsAnalyticsService.enrollment(params);
      setReport(data);
    } catch (error) {
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setReport(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleExport = async () => {
    if (activeTab === 'academic-performance' && !filters.academic_term_id) {
      toast.error('Select an academic term');
      return;
    }
    setExporting(true);
    try {
      const params = buildParams();
      if (activeTab === 'attendance') await reportsAnalyticsService.exportAttendance(params);
      else if (activeTab === 'fee-collection') await reportsAnalyticsService.exportFeeCollection(params);
      else if (activeTab === 'academic-performance') await reportsAnalyticsService.exportAcademicPerformance(params);
      else await reportsAnalyticsService.exportEnrollment(params);
    } catch (error) {
      toast.error(error.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const columns = report?.rows?.[0] ? Object.keys(report.rows[0]) : [];

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1><ChartBar size={26} weight="duotone" style={{ verticalAlign: 'middle', marginRight: 8 }} />Reports</h1>
            <p>Self-service reports with filters and CSV export</p>
          </div>
        </div>

        <div className="filters-bar">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`btn ${activeTab === t.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab(t.key)}
              style={{ marginBottom: 8 }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="form-card">
          <div className="form-row" style={{ flexWrap: 'wrap', gap: 12 }}>
            {(activeTab === 'attendance' || activeTab === 'fee-collection') && (
              <>
                <div className="form-group" style={{ minWidth: 160 }}>
                  <label>Start Date</label>
                  <input type="date" value={filters.start_date} onChange={e => setFilters({ ...filters, start_date: e.target.value })} />
                </div>
                <div className="form-group" style={{ minWidth: 160 }}>
                  <label>End Date</label>
                  <input type="date" value={filters.end_date} onChange={e => setFilters({ ...filters, end_date: e.target.value })} />
                </div>
              </>
            )}
            {activeTab === 'academic-performance' && (
              <div className="form-group" style={{ minWidth: 200 }}>
                <label>Academic Term</label>
                <select value={filters.academic_term_id} onChange={e => setFilters({ ...filters, academic_term_id: e.target.value })}>
                  <option value="">Select a term...</option>
                  {terms.map(t => {
                    const label = `${t.academic_year ?? ''} - ${t.term ? `Term ${t.term}` : 'Term'}`.trim();
                    return <option key={t.id} value={t.id}>{label}</option>;
                  })}
                </select>
              </div>
            )}
            {activeTab === 'fee-collection' && (
              <div className="form-group" style={{ minWidth: 160 }}>
                <label>Fee Type</label>
                <select value={filters.fee_type} onChange={e => setFilters({ ...filters, fee_type: e.target.value })}>
                  <option value="">All types</option>
                  <option value="tuition">Tuition</option>
                  <option value="examination">Examination</option>
                  <option value="sports">Sports</option>
                  <option value="ict">ICT</option>
                  <option value="library">Library</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="pta">PTA</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}
            {activeTab !== 'academic-performance' && (
              <div className="form-group" style={{ minWidth: 180 }}>
                <label>Class</label>
                <select value={filters.class_id} onChange={e => setFilters({ ...filters, class_id: e.target.value })}>
                  <option value="">All classes</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {activeTab === 'academic-performance' && (
              <div className="form-group" style={{ minWidth: 180 }}>
                <label>Class</label>
                <select value={filters.class_id} onChange={e => setFilters({ ...filters, class_id: e.target.value })}>
                  <option value="">All classes</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {campuses.length > 0 && (
              <div className="form-group" style={{ minWidth: 180 }}>
                <label>Campus</label>
                <select value={filters.campus_id} onChange={e => setFilters({ ...filters, campus_id: e.target.value })}>
                  <option value="">All campuses</option>
                  {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                {loading ? 'Running...' : 'Run Report'}
              </button>
              <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
                <Download size={16} /> {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>

        {report && (
          <>
            {report.summary && (
              <div className="stats-bar" style={{ marginBottom: 16 }}>
                {Object.entries(report.summary).map(([key, value]) => (
                  <div className="stat" key={key}>
                    <h3>{key.replace(/_/g, ' ')}</h3>
                    <p className="stat-value">{value}</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'fee-collection' && Array.isArray(report.by_month) && report.by_month.length > 0 && (
              <div className="table-container" style={{ marginBottom: 16, padding: 16 }}>
                <h3 style={{ margin: '0 0 12px' }}>Billed vs Collected by Month</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={report.by_month}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="amount" />
                    <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} unit="%" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="amount" dataKey="billed" fill="#94a3b8" name="Billed" />
                    <Bar yAxisId="amount" dataKey="collected" fill="#059669" name="Collected" />
                    <Line yAxisId="rate" type="monotone" dataKey="collection_rate" stroke="#0ea5e9" name="Collection Rate (%)" connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="table-container">
              {report.rows.length === 0 ? (
                <div className="empty-state"><p>No data for the selected filters</p></div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      {columns.map(col => <th key={col}>{col.replace(/_/g, ' ')}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row, i) => (
                      <tr key={i}>
                        {columns.map(col => <td key={col}>{row[col] ?? '-'}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default ReportsPage;
